import { X, Crown, Building2 } from 'lucide-react';
import { PxMark } from '@/components/PxMark';

interface LimitReachedModalProps {
    isOpen: boolean;
    onClose: () => void;
    planType: string;
    messagesRemainingToday: number;
    messagesRemainingMonth: number;
}

const UPGRADE_MAILTO = `mailto:hello@56k.studio?subject=${encodeURIComponent(
    'Pixel — passer au plan supérieur'
)}`;

const PLANS = [
    {
        id: 'pro',
        name: 'Pro',
        price: '25€',
        period: '/mois',
        credits: '100 crédits/mois',
        features: ['Projets privés', 'Sans badge', 'Rollover crédits'],
        icon: Crown,
        recommended: true,
    },
    {
        id: 'business',
        name: 'Business',
        price: '50€',
        period: '/mois',
        credits: '200 crédits/mois',
        features: ['SSO', 'Support prioritaire', 'Opt-out training'],
        icon: Building2,
        recommended: false,
    },
];

export function LimitReachedModal({
    isOpen,
    onClose,
    planType,
    messagesRemainingToday,
    messagesRemainingMonth,
}: LimitReachedModalProps) {
    if (!isOpen) return null;

    const isDaily = messagesRemainingToday <= 0 && messagesRemainingMonth > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
            <div className="w-full max-w-lg border border-hairline-strong bg-card">
                {/* Header */}
                <div className="relative border-b border-border px-6 py-8 text-center">
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="absolute right-4 top-4 p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                        <X className="h-4 w-4" />
                    </button>

                    <div className="flex flex-col items-center gap-3">
                        <PxMark state="idle" size={24} />
                        <span className="bg-warn/10 px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-warn">
                            Limite atteinte
                        </span>
                    </div>

                    <h2 className="mt-4 text-xl font-semibold tracking-[-0.005em] text-foreground">
                        {isDaily ? 'Limite journalière atteinte' : 'Limite de crédits atteinte'}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {isDaily
                            ? 'Revenez demain ou passez à un plan supérieur pour continuer.'
                            : 'Passez à un plan supérieur pour obtenir plus de crédits.'
                        }
                    </p>
                </div>

                {/* Plans */}
                <div className="space-y-3 px-6 py-6">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.id}
                            className={
                                plan.recommended
                                    ? 'border border-foreground bg-foreground p-4 text-background'
                                    : 'border border-border bg-card p-4 text-foreground'
                            }
                        >
                            <div className="flex items-baseline justify-between">
                                <div className="flex items-center gap-2">
                                    <plan.icon className="h-4 w-4" />
                                    <span className="text-sm font-medium">{plan.name}</span>
                                </div>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-xl font-semibold">{plan.price}</span>
                                    <span
                                        className={`font-mono text-[11px] ${
                                            plan.recommended ? 'text-background/60' : 'text-faint'
                                        }`}
                                    >
                                        {plan.period}
                                    </span>
                                </div>
                            </div>
                            <p
                                className={`mt-1 font-mono text-[11px] ${
                                    plan.recommended ? 'text-background/60' : 'text-faint'
                                }`}
                            >
                                {plan.credits}
                            </p>
                            <ul
                                className={`mt-3 space-y-1 font-mono text-[12px] ${
                                    plan.recommended ? 'text-background/80' : 'text-muted-foreground'
                                }`}
                            >
                                {plan.features.map((feature) => (
                                    <li key={feature}>{feature}</li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <a
                        href={UPGRADE_MAILTO}
                        className="flex h-10 w-full items-center justify-center bg-primary text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                        Passer au plan supérieur
                    </a>
                    <p className="text-center font-mono text-[11px] text-faint">Réponse sous 24h</p>
                </div>

                {/* Footer */}
                {isDaily && (
                    <div className="border-t border-border px-6 py-4 text-center">
                        <p className="font-mono text-[11px] text-faint">
                            Crédits restants ce mois : {messagesRemainingMonth}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
